<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useBranding } from 'shared/composables/useBranding';
import Icon from 'next/icon/Icon.vue';

import PageHeader from '../SettingsSubPageHeader.vue';

const { t, te } = useI18n();
const route = useRoute();
const { replaceInstallationName } = useBranding();

// Channel chosen in the wizard (route sub_page), shown in the step labels.
const channelName = computed(() => {
  const key = String(route.params.sub_page || '').toUpperCase();
  const path = `INBOX_MGMT.ADD.AUTH.CHANNEL.${key}.TITLE`;
  return key && te(path) ? t(path) : '';
});

const stepText = (step, field) => {
  if (step === 'INBOX' && channelName.value) {
    return t(`INBOX_MGMT.CREATE_FLOW.INBOX.CHANNEL_${field}`, {
      channel: channelName.value,
    });
  }
  return t(`INBOX_MGMT.CREATE_FLOW.${step}.${field}`);
};

const createFlowSteps = computed(() => {
  const steps = ['CHANNEL', 'INBOX', 'AGENT', 'FINISH'];
  const routes = {
    CHANNEL: 'settings_inbox_new',
    INBOX: 'settings_inboxes_page_channel',
    AGENT: 'settings_inboxes_add_agents',
    FINISH: 'settings_inbox_finish',
  };

  return steps.map(step => ({
    title: stepText(step, 'TITLE'),
    body: replaceInstallationName(stepText(step, 'BODY')),
    route: routes[step],
  }));
});

const currentStepIndex = computed(() => {
  const index = createFlowSteps.value.findIndex(
    item => item.route === route.name
  );
  return index < 0 ? 0 : index;
});

const isFirstStep = computed(() => route.name === 'settings_inbox_new');
const isFinishStep = computed(() => route.name === 'settings_inbox_finish');

const pageTitle = computed(() => {
  if (isFirstStep.value) return t('INBOX_MGMT.ADD.AUTH.TITLE');
  if (isFinishStep.value) return t('INBOX_MGMT.ADD.AUTH.TITLE_FINISH');
  return t('INBOX_MGMT.ADD.AUTH.TITLE_NEXT');
});

const progressLabel = computed(() =>
  t('INBOX_MGMT.ADD.AUTH.WIZARD.PROGRESS', {
    current: currentStepIndex.value + 1,
    total: createFlowSteps.value.length,
  })
);

const progressPercentage = computed(
  () => ((currentStepIndex.value + 1) / createFlowSteps.value.length) * 100
);
const progressPercentageLabel = computed(
  () => `${Math.round(progressPercentage.value)}%`
);
</script>

<template>
  <div class="mx-auto flex w-full max-w-7xl flex-col gap-5 px-4 pb-8 sm:px-6">
    <PageHeader class="block lg:hidden !mb-0" :header-title="pageTitle" />

    <div
      class="grid min-h-[720px] grid-cols-1 overflow-hidden rounded-3xl border border-n-slate-5 dark:border-n-slate-7 bg-n-solid-1 shadow-lg shadow-n-alpha-1 lg:grid-cols-[290px_minmax(0,1fr)]"
    >
      <aside
        class="relative hidden overflow-hidden border-r border-n-weak bg-n-alpha-1 px-6 py-7 lg:flex lg:flex-col"
      >
        <div
          class="pointer-events-none absolute -left-20 -top-24 size-64 rounded-full bg-n-brand/10 blur-3xl"
        />

        <div class="relative">
          <div
            class="mb-5 flex size-11 items-center justify-center rounded-2xl bg-n-brand text-white shadow-md shadow-n-brand/20"
          >
            <Icon icon="i-lucide-plug-zap" class="size-5" />
          </div>
          <p
            class="text-xs font-semibold uppercase tracking-[0.16em] text-n-brand"
          >
            {{ $t('INBOX_MGMT.ADD.AUTH.WIZARD.EYEBROW') }}
          </p>
          <h2 class="mt-2 text-lg font-semibold text-n-slate-12">
            {{ $t('INBOX_MGMT.ADD.AUTH.WIZARD.TITLE') }}
          </h2>
          <p class="mt-2 text-sm leading-6 text-n-slate-11">
            {{ $t('INBOX_MGMT.ADD.AUTH.WIZARD.DESCRIPTION') }}
          </p>
        </div>

        <div class="relative mt-7">
          <div class="mb-2 flex items-center justify-between text-xs">
            <span class="font-medium text-n-slate-12">{{ progressLabel }}</span>
            <span class="text-n-slate-10">{{ progressPercentageLabel }}</span>
          </div>
          <div class="h-1.5 overflow-hidden rounded-full bg-n-alpha-3">
            <div
              class="h-full rounded-full bg-n-brand transition-all duration-300"
              :style="{ width: `${progressPercentage}%` }"
            />
          </div>
        </div>

        <ol class="relative mt-7 flex flex-1 flex-col gap-2">
          <li
            v-for="(item, index) in createFlowSteps"
            :key="item.route"
            class="relative flex gap-3"
          >
            <div
              v-if="index < createFlowSteps.length - 1"
              class="absolute left-[17px] top-9 h-[calc(100%+0.75rem)] w-px"
              :class="index < currentStepIndex ? 'bg-n-brand/60' : 'bg-n-weak'"
            />
            <div
              class="relative z-10 flex size-9 shrink-0 items-center justify-center rounded-xl border text-xs font-semibold transition-colors"
              :class="{
                'border-n-brand bg-n-brand text-white':
                  index === currentStepIndex,
                'border-n-brand/30 bg-n-brand/10 text-n-brand':
                  index < currentStepIndex,
                'border-n-weak bg-n-solid-1 text-n-slate-10':
                  index > currentStepIndex,
              }"
            >
              <Icon
                v-if="index < currentStepIndex"
                icon="i-lucide-check"
                class="size-4"
              />
              <span v-else>{{ index + 1 }}</span>
            </div>
            <div
              class="min-w-0 flex-1 rounded-xl px-3 py-2 transition-colors"
              :class="{ 'bg-n-solid-1 shadow-sm': index === currentStepIndex }"
            >
              <p
                class="text-sm font-medium"
                :class="
                  index === currentStepIndex
                    ? 'text-n-brand'
                    : 'text-n-slate-12'
                "
              >
                {{ item.title }}
              </p>
              <p class="mt-1 text-xs leading-5 text-n-slate-10">
                {{ item.body }}
              </p>
            </div>
          </li>
        </ol>

        <div
          class="relative mt-5 rounded-2xl border border-n-weak bg-n-solid-1 p-3.5"
        >
          <div class="flex items-start gap-3">
            <div
              class="flex size-8 shrink-0 items-center justify-center rounded-xl bg-n-brand/10 text-n-brand"
            >
              <Icon icon="i-lucide-shield-check" class="size-4" />
            </div>
            <p class="text-xs leading-5 text-n-slate-11">
              {{ $t('INBOX_MGMT.ADD.AUTH.WIZARD.SECURITY_NOTE') }}
            </p>
          </div>
        </div>
      </aside>

      <main class="flex min-w-0 flex-col overflow-visible">
        <router-view />
      </main>
    </div>
  </div>
</template>
